import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import DocumentUpload from '../components/DocumentUpload';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionTier } from '../contexts/SubscriptionTierContext';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import { usePets } from '../hooks';
import { useMedicalRecords } from '../hooks/useMedicalRecords';
import type { MedicalRecord, RecordType } from '../types';
import { BASIC_VAULT_MAX_DOCUMENTS } from '../utils/constants';
import { formatDate } from '../utils/helpers';
import { countMedicalRecordsForUser } from '../utils/medicalRecordCount';

// ─── Record type config ───────────────────────────────────────────────────────

const TYPE_CONFIG: Record<RecordType, { emoji: string; label: string; color: string }> = {
  vaccine:      { emoji: '💉', label: 'Vaccination',   color: 'bg-green-500/20 text-green-300 border-green-500/30' },
  prescription: { emoji: '💊', label: 'Prescription',  color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  lab:          { emoji: '🔬', label: 'Lab Report',    color: 'bg-sky-500/20 text-sky-300 border-sky-500/30' },
};

// ─── Record card ──────────────────────────────────────────────────────────────

function RecordCard({
  record,
  onViewDocument,
}: {
  record: MedicalRecord;
  onViewDocument: (path: string) => void;
}) {
  const cfg = TYPE_CONFIG[record.record_type];

  return (
    <article className="glass-card p-4 animate-slide-up">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg border flex-shrink-0 ${cfg.color}`}>
          {cfg.emoji}
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg.color}`}>
              {cfg.label}
            </span>
            <span className="text-xs text-white/40">
              {formatDate(new Date(record.record_date))}
            </span>
          </div>

          {/* OCR text preview */}
          {record.extracted_text && (
            <p className="mt-2 text-sm text-white/60 line-clamp-2 leading-relaxed">
              {record.extracted_text}
            </p>
          )}

          {/* View document */}
          <button
            id={`view-doc-${record.id}`}
            onClick={() => onViewDocument(record.document_url)}
            className="mt-2 text-xs text-brand-400 hover:text-brand-300 font-medium transition-colors inline-flex items-center gap-1"
          >
            View document →
          </button>
        </div>
      </div>
    </article>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyRecords({
  onUpload,
  uploadDisabled,
}: {
  onUpload: () => void;
  uploadDisabled: boolean;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 animate-fade-in">
      <div className="w-20 h-20 rounded-full bg-brand-500/10 border-2 border-dashed border-brand-500/30 flex items-center justify-center mb-4">
        <span className="text-3xl">📋</span>
      </div>
      <h3 className="font-display text-xl font-bold text-white mb-2">No records yet</h3>
      <p className="text-white/40 text-sm max-w-xs mb-6">
        Upload your first vet document to start building this pet's health timeline.
      </p>
      <button
        id="empty-upload-btn"
        type="button"
        onClick={onUpload}
        disabled={uploadDisabled}
        title={uploadDisabled ? 'Loading vault usage…' : undefined}
        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span>📷</span> Upload first document
      </button>
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function RecordSkeleton() {
  return (
    <div className="glass-card p-4 flex gap-3 animate-pulse">
      <div className="w-10 h-10 rounded-xl bg-white/10 flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-28 bg-white/10 rounded" />
        <div className="h-3 w-48 bg-white/10 rounded" />
      </div>
    </div>
  );
}

// ─── Timeline connector ───────────────────────────────────────────────────────

function Timeline({ records, onViewDocument }: {
  records: MedicalRecord[];
  onViewDocument: (path: string) => void;
}) {
  return (
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-5 top-0 bottom-0 w-px bg-white/10 -z-0" />
      <div className="space-y-4 relative">
        {records.map((r) => (
          <RecordCard key={r.id} record={r} onViewDocument={onViewDocument} />
        ))}
      </div>
    </div>
  );
}

// ─── Pet Records Page ─────────────────────────────────────────────────────────

/**
 * PetRecordsPage — shows the medical history timeline for a single pet.
 * Accessible at /pets/:petId
 */
function PetRecordsPage() {
  const { petId } = useParams<{ petId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tier, ready } = useSubscriptionTier();
  const { showUpgrade } = useUpgradeModal();

  const { pets } = usePets();
  const pet = pets.find((p) => p.id === petId);

  const { records, isLoading, error, addRecord, getSignedUrl } = useMedicalRecords(petId ?? '');
  const [showUpload, setShowUpload] = useState(false);
  const [vaultDocCount, setVaultDocCount] = useState<number | null>(null);

  const refreshVaultDocCount = useCallback(async () => {
    if (!user?.id) {
      setVaultDocCount(null);
      return;
    }
    try {
      const n = await countMedicalRecordsForUser(user.id);
      setVaultDocCount(n);
    } catch {
      setVaultDocCount(null);
    }
  }, [user?.id]);

  useEffect(() => {
    void refreshVaultDocCount();
  }, [refreshVaultDocCount]);

  const docLimitReached =
    ready &&
    tier === 'basic' &&
    vaultDocCount !== null &&
    vaultDocCount >= BASIC_VAULT_MAX_DOCUMENTS;

  const uploadBusy = !ready || vaultDocCount === null;

  const openUpload = () => {
    if (uploadBusy) return;
    if (docLimitReached) {
      showUpgrade('documents');
      return;
    }
    setShowUpload(true);
  };

  const handleViewDocument = async (storagePath: string) => {
    const url = await getSignedUrl(storagePath);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleUpload = async (payload: Parameters<typeof addRecord>[0]) => {
    await addRecord(payload);
    await refreshVaultDocCount();
    setShowUpload(false);
  };

  return (
    <>
      <NavBar />

      <main className="page-container pt-20">

        {/* Back nav */}
        <button
          id="back-to-dashboard"
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-sm text-white/50 hover:text-white mb-5 transition-colors group"
        >
          <span className="group-hover:-translate-x-1 transition-transform">←</span>
          All pets
        </button>

        {/* Pet header */}
        {pet && (
          <div className="mb-4">
            <h1 className="font-display text-2xl font-bold text-white">{pet.name}</h1>
            <p className="text-white/40 text-sm mt-0.5 capitalize">{pet.species} · {pet.breed}</p>
          </div>
        )}

        {/* Quick nav to Reminders */}
        <button
          id="go-to-reminders"
          onClick={() => navigate(`/pets/${petId ?? ''}/reminders`)}
          className="flex items-center gap-2 mb-4 glass-card px-4 py-3 w-full text-left hover:bg-white/10 transition-all group"
        >
          <span className="text-xl">🔔</span>
          <div className="flex-1">
            <p className="text-sm font-medium text-white">Reminders</p>
            <p className="text-xs text-white/40">Medication &amp; vaccination alerts</p>
          </div>
          <span className="text-white/20 group-hover:text-brand-400 transition-colors">›</span>
        </button>

        {tier === 'basic' && ready && (
          <button
            type="button"
            id="ai-insights-teaser"
            onClick={() => showUpgrade('ai')}
            className="flex items-center gap-2 mb-6 w-full rounded-2xl border border-accent-500/25 bg-accent-500/5 px-4 py-3 text-left hover:bg-accent-500/10 transition-colors"
          >
            <span className="text-xl" aria-hidden>
              ✨
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white">AI Veterinary Insights</p>
              <p className="text-xs text-white/45">Premium Vault — smart summaries from your documents</p>
            </div>
            <span className="text-accent-400 text-sm font-semibold flex-shrink-0">Premium</span>
          </button>
        )}

        {/* Section header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Health Records</h2>
            {records.length > 0 && (
              <p className="text-white/40 text-xs mt-0.5">{records.length} document{records.length !== 1 ? 's' : ''}</p>
            )}
          </div>
          {records.length > 0 && (
            <button
              id="add-record-btn"
              type="button"
              disabled={uploadBusy}
              onClick={openUpload}
              title={
                uploadBusy
                  ? 'Loading vault usage…'
                  : docLimitReached
                    ? 'Basic Vault document limit — upgrade for unlimited storage'
                    : undefined
              }
              className={`btn-primary py-2 px-4 text-sm ${docLimitReached && !uploadBusy ? 'opacity-80' : ''}`}
            >
              <span>📷</span> Upload
            </button>
          )}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="space-y-3">
            {[1, 2].map((n) => <RecordSkeleton key={n} />)}
          </div>
        )}

        {/* Error */}
        {!isLoading && error && (
          <div role="alert" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-2xl px-5 py-4">
            <p className="font-semibold mb-1">Failed to load records</p>
            <p className="text-red-400/70">{error}</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && records.length === 0 && (
          <EmptyRecords onUpload={openUpload} uploadDisabled={uploadBusy} />
        )}

        {/* Timeline */}
        {!isLoading && records.length > 0 && (
          <Timeline records={records} onViewDocument={(path) => void handleViewDocument(path)} />
        )}
      </main>

      {/* Upload modal */}
      {showUpload && petId && (
        <DocumentUpload
          petId={petId}
          onSubmit={(payload) => handleUpload(payload)}
          onClose={() => setShowUpload(false)}
        />
      )}
    </>
  );
}

export default PetRecordsPage;
